import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/api/axios'
import { useToast } from 'vue-toastification'

export const useCallStore = defineStore('calls', () => {
  const toast = useToast()
  
  // State
  const calls = ref([])
  const currentCall = ref(null)
  const activeCall = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const callStats = ref({
    total: 0,
    completed: 0,
    missed: 0,
    inProgress: 0
  })
  
  // Getters
  const todaysCalls = computed(() => {
    const today = new Date().toDateString()
    return calls.value.filter(call => 
      new Date(call.created_at).toDateString() === today
    )
  })
  
  const callsByProfile = computed(() => (profileId) => {
    return calls.value.filter(call => 
      call.profile_id === parseInt(profileId)
    )
  })
  
  const recentCalls = computed(() => {
    return [...calls.value]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 10)
  })
  
  // Actions
  async function fetchCalls(params = {}) {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get('/calls', { params })
      calls.value = response.data.calls || []
      
      // Map backend stats to frontend format
      const backendStats = response.data.stats || {}
      callStats.value = {
        total: backendStats.total || 0,
        completed: backendStats.completed || 0,
        missed: backendStats.missed || 0,
        inProgress: backendStats.inProgress || backendStats.in_progress || 0
      }
      
      return response.data
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to fetch calls'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function fetchCall(id) {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get(`/calls/${id}`)
      currentCall.value = response.data.call
      
      // Update in list if exists
      const index = calls.value.findIndex(c => c.id === parseInt(id))
      if (index !== -1) {
        calls.value[index] = response.data.call
      }
      
      return response.data.call
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to fetch call'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function initiateCall(profileId, options = {}) {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post('/calls/initiate', {
        profile_id: profileId,
        ...options
      })
      
      activeCall.value = response.data.call
      calls.value.unshift(response.data.call)
      toast.success('Call initiated successfully!')
      
      // Start polling for status updates
      if (response.data.call?.id) {
        pollCallStatus(response.data.call.id)
      }
      
      return response.data
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to initiate call'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function endCall(callId, notes = '') {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post(`/calls/${callId}/end`, { notes })
      
      // Update call in list
      const index = calls.value.findIndex(c => c.id === parseInt(callId))
      if (index !== -1) {
        calls.value[index] = response.data.call
      }
      
      if (activeCall.value?.id === parseInt(callId)) {
        activeCall.value = null
      }
      
      toast.success('Call ended successfully!')
      return response.data.call
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to end call'
      toast.error(error.value)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function fetchTodaysCalls() {
    const today = new Date().toISOString().split('T')[0]
    return fetchCalls({ date: today })
  }
  
  async function fetchCallTranscript(callId) {
    try {
      const response = await api.get(`/calls/${callId}/transcript`)
      return response.data.transcript
    } catch (err) {
      toast.error('Failed to fetch transcript')
      throw err
    }
  }
  
  async function fetchCallAnalysis(callId) {
    try {
      const response = await api.get(`/calls/${callId}/analysis`)
      return response.data.analysis
    } catch (err) {
      toast.error('Failed to fetch analysis')
      throw err
    }
  }
  
  async function downloadCallRecording(callId) {
    try {
      const response = await api.get(`/calls/${callId}/recording`, {
        responseType: 'blob'
      })
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `call-${callId}.mp3`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      toast.success('Recording downloaded successfully!')
    } catch (err) {
      toast.error('Failed to download recording')
      throw err
    }
  }

  async function refreshCallStatus(callId) {
    try {
      const response = await api.get(`/calls/${callId}`)
      const updatedCall = response.data.call || response.data
      
      // Update the call in the list
      const index = calls.value.findIndex(c => c.id === parseInt(callId))
      if (index !== -1) {
        calls.value[index] = updatedCall
      }
      
      // Update active call if it matches
      if (activeCall.value?.id === parseInt(callId)) {
        activeCall.value = updatedCall
      }
      
      return updatedCall
    } catch (err) {
      console.error('Failed to refresh call status:', err)
      // If individual call fetch fails, just refresh all calls
      try {
        await fetchCalls()
      } catch (fetchError) {
        console.error('Failed to refresh calls list:', fetchError)
      }
      throw err
    }
  }

  async function pollCallStatus(callId, intervalMs = 5000) {
    const pollInterval = setInterval(async () => {
      try {
        const call = await refreshCallStatus(callId)
        
        // Stop polling if call is completed or failed
        if (call.status === 'completed' || call.status === 'missed' || call.status === 'failed') {
          clearInterval(pollInterval)
          toast.success(`Call ${call.status}!`)
          
          // Refresh full calls list to update stats
          await fetchCalls()
        }
      } catch (error) {
        console.error('Error polling call status:', error)
        clearInterval(pollInterval)
      }
    }, intervalMs)
    
    return pollInterval
  }
  
  return {
    // State
    calls,
    currentCall,
    activeCall,
    loading,
    error,
    callStats,
    
    // Getters
    todaysCalls,
    callsByProfile,
    recentCalls,
    
    // Actions
    fetchCalls,
    fetchCall,
    initiateCall,
    endCall,
    fetchTodaysCalls,
    fetchCallTranscript,
    fetchCallAnalysis,
    downloadCallRecording,
    refreshCallStatus,
    pollCallStatus
  }
})